import React, { useState, useEffect, useCallback } from 'react';
import { auditApi } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { formatDistanceToNow } from 'date-fns';

const ACTION_COLORS = {
  LOGIN: 'var(--accent-cyan)',
  CONNECT_CAMERA: 'var(--accent-green)',
  STREAM_CONNECT: 'var(--accent-green)',
  SNAPSHOT: 'var(--accent-purple)',
  PTZ_PRESET: 'var(--accent-amber)',
  ADD_CAMERA: 'var(--accent-cyan)',
  UPDATE_CAMERA: 'var(--accent-amber)',
  DELETE_CAMERA: 'var(--accent-red)',
  CHANGE_PASSWORD: 'var(--accent-amber)',
  CREATE_USER: 'var(--accent-cyan)',
};

const ALL_ACTIONS = Object.keys(ACTION_COLORS);

export default function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCamera, setFilterCamera] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const { addToast, cameras } = useAppStore();

  const load = useCallback(async () => {
    try {
      const res = await auditApi.getLog(500, filterCamera || null, filterAction || null);
      setLogs(res.data);
    } catch {
      addToast({ type: 'error', message: 'Failed to load audit log' });
    } finally {
      setLoading(false);
    }
  }, [filterCamera, filterAction, addToast]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  const selectStyle = {
    height: '32px',
    padding: '0 8px',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-dim)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-mono)',
    fontSize: '11px',
    cursor: 'pointer',
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '16px', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-cyan)', textTransform: 'uppercase' }}>
          Audit Log
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={selectStyle} value={filterCamera} onChange={e => setFilterCamera(e.target.value)}>
            <option value="">All Cameras</option>
            {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select style={selectStyle} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">All Actions</option>
            {ALL_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
          </select>

          {(filterCamera || filterAction) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilterCamera(''); setFilterAction(''); }}>
              Clear
            </button>
          )}

          <button className="btn btn-ghost btn-sm" onClick={load}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
            Refresh
          </button>
        </div>
      </div>

      <div style={{
        flex: 1,
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-dim)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Table header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 120px 160px 1fr',
          padding: '10px 16px',
          background: 'var(--bg-elevated)',
          borderBottom: '1px solid var(--border-dim)',
          fontFamily: 'var(--font-display)',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}>
          <span>Timestamp</span>
          <span>User</span>
          <span>Action</span>
          <span>Details</span>
        </div>

        {/* Table rows */}
        <div className="scrollable" style={{ flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
              Loading audit log...
            </div>
          )}
          {!loading && logs.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>
              No audit entries{filterCamera || filterAction ? ' matching filters' : ''}
            </div>
          )}
          {logs.map((log, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '140px 120px 160px 1fr',
              padding: '9px 16px',
              borderBottom: '1px solid var(--border-dim)',
              alignItems: 'center',
              transition: 'background 0.1s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)' }}>
                <div>{new Date(log.timestamp).toLocaleTimeString()}</div>
                <div style={{ fontSize: '9px', opacity: 0.6 }}>
                  {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                </div>
              </div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '0.04em' }}>
                {log.username || 'system'}
              </div>
              <div>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  padding: '2px 8px',
                  borderRadius: '3px',
                  background: `${ACTION_COLORS[log.action] || 'var(--text-muted)'}22`,
                  color: ACTION_COLORS[log.action] || 'var(--text-muted)',
                  border: `1px solid ${ACTION_COLORS[log.action] || 'var(--text-muted)'}44`,
                }}>
                  {log.action}
                </span>
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {log.details}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
